import { db } from "@/lib/firebase";
import { collection, doc, getDoc, getDocs, setDoc, updateDoc, query, orderBy, writeBatch, where } from "firebase/firestore";
import { Lead, HistoryEntry, LeadStatus } from "@/components/lead-card";
import { User } from "./userService";
import { startOfDay, subDays, isWithinInterval, addWeeks, addDays, startOfMonth, endOfMonth, addMonths } from "date-fns";
import { logger } from "@/lib/logger";

// Represents the structure of a configurable score action
export interface ScoreActionConfig {
  id: string; // Document ID, e.g., "new_lead_generated"
  actionName: string;
  points: number;
  details: string;
  trigger: {
    type: "lead_status_change" | "lead_status_transition" | "manual_action";
    status?: LeadStatus | string; // e.g., "new", "scheduled", "won"
    fromStatus?: LeadStatus | string;
    toStatus?: LeadStatus | string;
    action_code?: string; // e.g., "recruiting_contact_added"
  };
  order: number; // To maintain a consistent display order
}

// Represents a user's score details for the scoreboard
export interface UserScore {
  userId: string;
  userName: string;
  totalPoints: number;
  allTimeHigh: number;  // Highest score ever achieved
  weeklyHigh: number;   // Highest score in the current week
  monthlyHigh: number;  // Highest score in the current month
  lastUpdated: Date;    // When the score was last updated
  lastWeekReset: Date;  // When the weekly score was last reset
  lastMonthReset: Date; // When the monthly score was last reset
  currentWeekPoints: number; // Points earned in the current week
  currentMonthPoints: number; // Points earned in the current month
  tenantId?: string;
}

const SCORE_CONFIG_COLLECTION = "score_configurations";

// Predefined actions to ensure they exist in Firestore
// We will use these IDs as document IDs in Firestore
export const PREDEFINED_ACTIONS: Omit<ScoreActionConfig, 'points'>[] = [
  {
    id: "new_lead_generated",
    actionName: "Neuen Lead generiert",
    details: "Lead erhält Status 'Neu' oder wird erstellt.",
    trigger: { type: "lead_status_change", status: "new" },
    order: 1,
  },
  {
    id: "appointment_scheduled",
    actionName: "Termin vereinbart",
    details: "Lead erhält Status 'Terminiert' (Scheduled)",
    trigger: { type: "lead_status_change", status: "scheduled" },
    order: 2,
  },
  {
    id: "consultation_held",
    actionName: "Beratung stattgefunden",
    details: "Lead wechselt von 'Terminiert' zu 'Wiedervorlage' (Follow-up)",
    trigger: { type: "lead_status_transition", fromStatus: "scheduled", toStatus: "follow-up" },
    order: 3,
  },
  {
    id: "deal_closed",
    actionName: "Abschluss",
    details: "Lead erhält Status 'Gewonnen' (Won)",
    trigger: { type: "lead_status_change", status: "won" },
    order: 4,
  },
  {
    id: "recruiting_contact_added",
    actionName: "Recruiting Kontakt eingestellt",
    details: "Spezieller Recruiting-Kontakt wird erfasst (manuelle Aktion oder spezifischer Lead-Typ)",
    trigger: { type: "manual_action", action_code: "recruiting_contact_added" },
    order: 5,
  },
  {
    id: "invitation_praktikant_activated",
    actionName: "Praktikant Einladung",
    details: "250 Punkte für aktivierte Praktikant/Dualer Student Einladung",
    trigger: { type: "manual_action", action_code: "invitation_praktikant_activated" },
    order: 6,
  },
  {
    id: "invitation_berater_activated",
    actionName: "Berater Einladung",
    details: "1000 Punkte für aktivierte Berater/Trainee/Beraterassistent Einladung",
    trigger: { type: "manual_action", action_code: "invitation_berater_activated" },
    order: 7,
  },
];

// Default points for initial setup if a config is missing
const DEFAULT_POINTS: { [key: string]: number } = {
  new_lead_generated: 5,
  appointment_scheduled: 1,
  consultation_held: 5,
  deal_closed: 20,
  recruiting_contact_added: 100,
  invitation_praktikant_activated: 250,
  invitation_berater_activated: 1000,
};

/**
 * Initializes the score configurations in Firestore if they don't exist.
 * Also removes deprecated configurations on first run.
 * This should be called once, perhaps on app startup or from an admin setup page.
 */
export async function initializeScoreConfiguration(): Promise<void> {
  try {
    // Check if we need to remove deprecated configs (one-time cleanup)
    const deprecatedDocRef = doc(db, SCORE_CONFIG_COLLECTION, 'user_invitation_activated');
    const deprecatedDocSnap = await getDoc(deprecatedDocRef);

    if (deprecatedDocSnap.exists()) {
      logger.log("Found deprecated score configurations, cleaning up...");
      await removeDeprecatedScoreConfigs();
    }

    // Initialize new configurations
    for (const action of PREDEFINED_ACTIONS) {
      const docRef = doc(db, SCORE_CONFIG_COLLECTION, action.id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        await setDoc(docRef, {
          ...action,
          points: DEFAULT_POINTS[action.id] || 0, // Set default points
        });
        logger.log(`Initialized score configuration for: ${action.id}`);
      }
    }
  } catch (error) {
    logger.error("Error initializing score configurations:", error);
    // Optionally, rethrow or handle more gracefully
  }
}

/**
 * Fetches all score action configurations, ordered by 'order'.
 */
export async function getScoreActionConfigs(): Promise<ScoreActionConfig[]> {
  try {
    const q = query(collection(db, SCORE_CONFIG_COLLECTION), orderBy("order"));
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      // If empty, try to initialize and then re-fetch
      logger.log("Score configurations not found, attempting to initialize...");
      await initializeScoreConfiguration();
      const newSnapshot = await getDocs(q);
      return newSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ScoreActionConfig));
    }
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ScoreActionConfig));
  } catch (error) {
    logger.error("Error fetching score action configurations:", error);
    return []; // Return empty array on error
  }
}

/**
 * Updates the points for a specific score action configuration.
 * @param actionId The ID of the action config to update (e.g., "new_lead_generated")
 * @param points The new point value.
 */
export async function updateScoreActionPoints(actionId: string, points: number): Promise<boolean> {
  try {
    if (points < 0) throw new Error("Points cannot be negative.");
    const docRef = doc(db, SCORE_CONFIG_COLLECTION, actionId);
    await updateDoc(docRef, { points });
    logger.log(`Updated points for ${actionId} to ${points}`);
    return true;
  } catch (error) {
    logger.error(`Error updating points for ${actionId}:`, error);
    return false;
  }
}

/**
 * Calculates the score for a single lead based on its current status and history,
 * against the provided score configurations.
 * 
 * IMPORTANT: This is a simplified initial version. 
 * For one-time events (like 'new_lead_generated'), this might award points every time it's calculated
 * if the lead is still in 'new' status. More sophisticated tracking (e.g., has this point already been awarded for this lead?)
 * might be needed for perfect accuracy, potentially by storing awarded points log or checking creation date from history.
 */
function calculateScoreForLead(lead: Lead, configs: ScoreActionConfig[]): number {
  let leadScore = 0;

  for (const config of configs) {
    switch (config.trigger.type) {
      case "lead_status_change":
        if (config.id === "new_lead_generated") {
          // Special handling for "new_lead_generated":
          // Award points if the lead's first history entry is 'created',
          // and the config is indeed for the "new" status.
          // This signifies it was a "new lead" and its points should be granted.
          if (config.trigger.status === "new" && lead.history && lead.history.length > 0 && lead.history[0].type === "created") {
            leadScore += config.points;
          }
        } else {
          // For all other "lead_status_change" types (e.g., "won", "scheduled"):
          // Award points if the lead has ever reached this status (check history), not just current status.
          let statusEverReached = false;

          // Check current status first
          if (lead.status === config.trigger.status) {
            statusEverReached = true;
          } else if (lead.history) {
            // Check history for this status ever being reached
            for (const entry of lead.history) {
              if (entry.type === "updated") {
                const statusChange = entry.changes.find(c => c.field === "status");
                if (statusChange &&
                  (statusChange.newValue === config.trigger.status ||
                    statusChange.oldValue === config.trigger.status)) {
                  statusEverReached = true;
                  break;
                }
              }
            }
          }

          if (statusEverReached) {
            leadScore += config.points;
          }
        }
        break;

      case "lead_status_transition":
        if (lead.history && config.trigger.fromStatus && config.trigger.toStatus) {
          // Check the entire history for the specific transition occuring at least once.
          let transitionOccurred = false;
          for (const entry of lead.history) {
            if (entry.type === "updated") {
              const statusChange = entry.changes.find(c => c.field === "status");
              if (statusChange && statusChange.oldValue === config.trigger.fromStatus && statusChange.newValue === config.trigger.toStatus) {
                transitionOccurred = true;
                break; // Found the transition, no need to check further for this lead for this config
              }
            }
          }
          if (transitionOccurred) {
            leadScore += config.points;
          }
        }
        break;

      case "manual_action":
        // Placeholder for manual actions - requires specific implementation based on how these are tracked.
        break;
    }
  }
  return leadScore;
}

/**
 * Calculates total scores for all users, filtered by position if specified (e.g., 'Praktikant').
 * Now includes all-time high, weekly high, and monthly high calculations with proper resets.
 */
export async function calculateAllUserScores(
  users: User[],
  leads: Lead[],
  configs: ScoreActionConfig[],
  positionFilter?: string,
  selectedDate?: Date,
  tenantId: string = "legacy"
): Promise<UserScore[]> {
  try {
    logger.log("Starting calculateAllUserScores with:", {
      userCount: users.length,
      leadCount: leads.length,
      configCount: configs.length,
      positionFilter,
      selectedDate: selectedDate?.toISOString(),
      tenantId
    });

    const scores: UserScore[] = [];
    const now = new Date();
    // Use selected date if provided, otherwise use current date
    const referenceDate = selectedDate || now;

    // Check if the selected date is in the future
    const isFutureDate = referenceDate > now;
    logger.log("Date check:", {
      referenceDate: referenceDate.toISOString(),
      now: now.toISOString(),
      isFutureDate
    });

    // Calculate date ranges based on reference date
    const startOfWeek = startOfDay(subDays(referenceDate, referenceDate.getDay())); // Start of selected week (Sunday)
    const endOfWeek = addDays(startOfWeek, 6); // End of selected week (Saturday)
    const startOfMonth = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1); // Start of selected month
    const endOfMonth = new Date(referenceDate.getFullYear(), referenceDate.getMonth() + 1, 0); // End of selected month

    logger.log("Using date ranges:", {
      startOfWeek: startOfWeek.toISOString(),
      endOfWeek: endOfWeek.toISOString(),
      startOfMonth: startOfMonth.toISOString(),
      endOfMonth: endOfMonth.toISOString(),
      referenceDate: referenceDate.toISOString(),
      isFutureDate
    });

    // Get existing scores from Firestore to maintain all-time highs
    const scoresRef = collection(db, "userScores");
    let q;
    if (tenantId === "legacy") {
      q = query(scoresRef);
    } else {
      q = query(scoresRef, where("tenantId", "==", tenantId));
    }

    const scoresSnapshot = await getDocs(q);
    logger.log("Existing scores in Firestore:", scoresSnapshot.size);

    const existingScores = new Map<string, UserScore>();

    scoresSnapshot.forEach(doc => {
      const data = doc.data() as UserScore;
      // Filter legacy if needed
      if (tenantId === "legacy" && data.tenantId && data.tenantId !== "legacy") return;

      existingScores.set(data.userId, {
        ...data,
        lastUpdated: data.lastUpdated instanceof Date ? data.lastUpdated : new Date(data.lastUpdated),
        lastWeekReset: data.lastWeekReset instanceof Date ? data.lastWeekReset : new Date(data.lastWeekReset),
        lastMonthReset: data.lastMonthReset instanceof Date ? data.lastMonthReset : new Date(data.lastMonthReset)
      });
    });

    for (const user of users) {
      if (positionFilter && user.position !== positionFilter) {
        continue;
      }

      // Skip deactivated users
      if (user.active === false) {
        continue;
      }

      // Get user's leads - filter by creator (history[0].user) instead of assignedTo
      const userLeads = leads.filter(lead => {
        // First check if lead has creation history entry
        const creationEntry = lead.history?.find(entry => entry.type === 'created');
        if (creationEntry && creationEntry.user) {
          return creationEntry.user === user.id;
        }
        // Fallback to assignedTo for leads without proper history (backward compatibility)
        return lead.assignedTo === user.id;
      });

      // Calculate all-time points (always calculated regardless of date)
      const allTimePoints = userLeads.reduce((total, lead) => {
        return total + calculateScoreForLead(lead, configs);
      }, 0);

      // For future dates, set all period-specific points to 0
      let weeklyPoints = 0;
      let monthlyPoints = 0;

      if (!isFutureDate) {
        // Calculate weekly points (only for leads created in the selected week)
        weeklyPoints = userLeads.reduce((total, lead) => {
          const creationDate = lead.history?.find(entry => entry.type === 'created')?.date;
          if (creationDate) {
            const leadDate = new Date(creationDate);
            // Only count points if the lead was created within the selected week
            if (leadDate >= startOfWeek && leadDate <= endOfWeek) {
              const points = calculateScoreForLead(lead, configs);
              logger.log(`Lead ${lead.id} created in selected week: ${leadDate.toISOString()}, points: ${points}`);
              return total + points;
            }
          }
          return total;
        }, 0);

        // Calculate monthly points (only for leads created in the selected month)
        monthlyPoints = userLeads.reduce((total, lead) => {
          const creationDate = lead.history?.find(entry => entry.type === 'created')?.date;
          if (creationDate) {
            const leadDate = new Date(creationDate);
            // Only count points if the lead was created within the selected month
            if (leadDate >= startOfMonth && leadDate <= endOfMonth) {
              const points = calculateScoreForLead(lead, configs);
              logger.log(`Lead ${lead.id} created in selected month: ${leadDate.toISOString()}, points: ${points}`);
              return total + points;
            }
          }
          return total;
        }, 0);
      } else {
        logger.log(`Future date detected, setting period points to 0 for user ${user.id}`);
      }

      logger.log("Calculated points for user:", {
        userId: user.id,
        allTimePoints,
        weeklyPoints,
        monthlyPoints,
        isFutureDate,
        startOfWeek: startOfWeek.toISOString(),
        endOfWeek: endOfWeek.toISOString(),
        startOfMonth: startOfMonth.toISOString(),
        endOfMonth: endOfMonth.toISOString()
      });

      // Get existing score data or initialize new
      const existingScore = existingScores.get(user.id);
      const lastUpdated = existingScore?.lastUpdated || now;

      // Detect if we need to reset weekly/monthly points (new period started)
      const needsWeeklyReset = !existingScore?.lastWeekReset ||
        startOfWeek.getTime() > new Date(existingScore.lastWeekReset).getTime();
      const needsMonthlyReset = !existingScore?.lastMonthReset ||
        startOfMonth.getTime() > new Date(existingScore.lastMonthReset).getTime();

      // If this is a period reset, preserve the high scores before resetting current points
      let currentWeekPoints = weeklyPoints;
      let currentMonthPoints = monthlyPoints;
      let weeklyHigh = existingScore?.weeklyHigh || 0;
      let monthlyHigh = existingScore?.monthlyHigh || 0;

      // Update weekly high score if current week performance is better
      if (needsWeeklyReset && existingScore?.currentWeekPoints) {
        // Preserve the previous week's performance as potential high score
        weeklyHigh = Math.max(weeklyHigh, existingScore.currentWeekPoints);
        // Reset current week points for new period
        currentWeekPoints = weeklyPoints;
      } else if (!needsWeeklyReset) {
        // Still in same week, keep accumulating current week points
        currentWeekPoints = weeklyPoints;
        weeklyHigh = Math.max(weeklyHigh, currentWeekPoints);
      }

      // Update monthly high score if current month performance is better
      if (needsMonthlyReset && existingScore?.currentMonthPoints) {
        // Preserve the previous month's performance as potential high score
        monthlyHigh = Math.max(monthlyHigh, existingScore.currentMonthPoints);
        // Reset current month points for new period
        currentMonthPoints = monthlyPoints;
      } else if (!needsMonthlyReset) {
        // Still in same month, keep accumulating current month points
        currentMonthPoints = monthlyPoints;
        monthlyHigh = Math.max(monthlyHigh, currentMonthPoints);
      }

      // Calculate all-time high as the highest performance ever achieved (weekly OR monthly)
      // This should be the absolute highest score the user has ever reached
      const allTimeHigh = existingScore
        ? Math.max(existingScore.allTimeHigh, weeklyHigh, monthlyHigh, currentWeekPoints, currentMonthPoints)
        : Math.max(weeklyHigh, monthlyHigh, currentWeekPoints, currentMonthPoints);

      const userScore = {
        userId: user.id,
        userName: user.name || user.email || "Unbekannter Benutzer",
        totalPoints: currentMonthPoints, // Use monthly points as "total" (includes weekly), not lifetime cumulative
        allTimeHigh,
        weeklyHigh,
        monthlyHigh,
        lastUpdated: now,
        lastWeekReset: startOfWeek,
        lastMonthReset: startOfMonth,
        currentWeekPoints,
        currentMonthPoints,
        tenantId
      };

      logger.log("Final score for user:", userScore);

      scores.push(userScore);

      // Update the score in Firestore
      const userScoreRef = doc(db, "userScores", user.id);
      try {
        await setDoc(userScoreRef, userScore, { merge: true });
        logger.log("Successfully updated score in Firestore for user:", user.id);
      } catch (error) {
        logger.error("Error updating score in Firestore for user:", user.id, error);
      }
    }

    // Sort by total points in descending order
    const sortedScores = scores.sort((a, b) => b.totalPoints - a.totalPoints);
    logger.log("Final sorted scores:", sortedScores);
    return sortedScores;
  } catch (error) {
    logger.error("Error calculating user scores:", error);
    throw error;
  }
}

/**
 * DEV ONLY: Manually resets weekly scores for all users
 * This function should only be used in development/testing
 */
export async function devResetWeeklyScores(): Promise<void> {
  try {
    logger.log("DEV: Starting manual weekly score reset");
    const scoresRef = collection(db, "userScores");
    const scoresSnapshot = await getDocs(scoresRef);

    const batch = writeBatch(db);
    const now = new Date();
    const startOfWeek = startOfDay(subDays(now, now.getDay()));

    scoresSnapshot.forEach(doc => {
      const data = doc.data() as UserScore;
      const userScoreRef = doc.ref;

      batch.update(userScoreRef, {
        currentWeekPoints: 0,
        lastWeekReset: startOfWeek,
        lastUpdated: now
      });
    });

    await batch.commit();
    logger.log("DEV: Successfully reset weekly scores for all users");
  } catch (error) {
    logger.error("DEV: Error resetting weekly scores:", error);
    throw error;
  }
}

/**
 * DEV ONLY: Manually resets monthly scores for all users
 * This function should only be used in development/testing
 */
export async function devResetMonthlyScores(): Promise<void> {
  try {
    logger.log("DEV: Starting manual monthly score reset");
    const scoresRef = collection(db, "userScores");
    const scoresSnapshot = await getDocs(scoresRef);

    const batch = writeBatch(db);
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    scoresSnapshot.forEach(doc => {
      const data = doc.data() as UserScore;
      const userScoreRef = doc.ref;

      batch.update(userScoreRef, {
        currentMonthPoints: 0,
        lastMonthReset: startOfMonth,
        lastUpdated: now
      });
    });

    await batch.commit();
    logger.log("DEV: Successfully reset monthly scores for all users");
  } catch (error) {
    logger.error("DEV: Error resetting monthly scores:", error);
    throw error;
  }
}

/**
 * Calculate historical weekly and monthly highs for a user
 * Scans all historical periods to find the user's best performances
 */
function calculateHistoricalHighs(userLeads: Lead[], configs: ScoreActionConfig[]): { weeklyHigh: number; monthlyHigh: number } {
  if (userLeads.length === 0) {
    return { weeklyHigh: 0, monthlyHigh: 0 };
  }

  // Extract all creation dates
  const creationDates = userLeads
    .map(lead => {
      const creationEntry = lead.history?.find(entry => entry.type === 'created');
      return creationEntry?.date ? new Date(creationEntry.date) : null;
    })
    .filter(date => date !== null) as Date[];

  if (creationDates.length === 0) {
    return { weeklyHigh: 0, monthlyHigh: 0 };
  }

  // Find date range for historical scanning
  const firstDate = new Date(Math.min(...creationDates.map(d => d.getTime())));
  const lastDate = new Date();

  let weeklyHigh = 0;
  let monthlyHigh = 0;

  // Scan all weeks from first lead to now
  let currentWeekStart = startOfDay(subDays(firstDate, firstDate.getDay()));
  while (currentWeekStart <= lastDate) {
    const currentWeekEnd = addDays(currentWeekStart, 6);

    // Calculate points for this specific week
    const weekPoints = userLeads.reduce((total, lead) => {
      const creationEntry = lead.history?.find(entry => entry.type === 'created');
      if (creationEntry && creationEntry.date) {
        const creationDate = new Date(creationEntry.date);
        if (creationDate >= currentWeekStart && creationDate <= currentWeekEnd) {
          return total + calculateScoreForLead(lead, configs);
        }
      }
      return total;
    }, 0);

    weeklyHigh = Math.max(weeklyHigh, weekPoints);
    currentWeekStart = addWeeks(currentWeekStart, 1);
  }

  // Scan all months from first lead to now
  let currentMonthStart = new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);
  while (currentMonthStart <= lastDate) {
    const currentMonthEnd = new Date(currentMonthStart.getFullYear(), currentMonthStart.getMonth() + 1, 0);

    // Calculate points for this specific month
    const monthPoints = userLeads.reduce((total, lead) => {
      const creationEntry = lead.history?.find(entry => entry.type === 'created');
      if (creationEntry && creationEntry.date) {
        const creationDate = new Date(creationEntry.date);
        if (creationDate >= currentMonthStart && creationDate <= currentMonthEnd) {
          return total + calculateScoreForLead(lead, configs);
        }
      }
      return total;
    }, 0);

    monthlyHigh = Math.max(monthlyHigh, monthPoints);
    currentMonthStart = new Date(currentMonthStart.getFullYear(), currentMonthStart.getMonth() + 1, 1);
  }

  return { weeklyHigh, monthlyHigh };
}

/**
 * DEV ONLY: Recalculate all user scores from scratch over entire timespan
 * This function should only be used in development/testing to fix all score data
 */
export async function devRecalculateAllScores(): Promise<void> {
  try {
    logger.log("DEV: Starting complete score recalculation");

    // Get all users and leads
    const { getPraktikanten } = await import('./userService');
    const { fetchLeads } = await import('./leadsService');

    const praktikanten = await getPraktikanten();
    const allLeads = await fetchLeads();
    const configs = await getScoreActionConfigs();

    if (praktikanten.length === 0 || configs.length === 0) {
      logger.log("DEV: No users or configs found for recalculation");
      return;
    }

    logger.log(`DEV: Recalculating scores for ${praktikanten.length} users with ${allLeads.length} leads`);

    const batch = writeBatch(db);
    const now = new Date();

    // Calculate current date ranges
    const currentDate = new Date();
    const startOfCurrentWeek = startOfDay(subDays(currentDate, currentDate.getDay()));
    const endOfCurrentWeek = addDays(startOfCurrentWeek, 6);
    const startOfCurrentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endOfCurrentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

    for (const user of praktikanten) {
      // Get user's leads (using correct attribution)
      const userLeads = allLeads.filter(lead => {
        const creationEntry = lead.history?.find(entry => entry.type === 'created');
        if (creationEntry && creationEntry.user) {
          return creationEntry.user === user.id;
        }
        return lead.assignedTo === user.id;
      });

      // Calculate total points from all user's leads (entire timespan)
      const totalPoints = userLeads.reduce((total, lead) => {
        return total + calculateScoreForLead(lead, configs);
      }, 0);

      // Calculate current week points (leads created this week)
      const currentWeekPoints = userLeads.reduce((total, lead) => {
        const creationEntry = lead.history?.find(entry => entry.type === 'created');
        if (creationEntry && creationEntry.date) {
          const creationDate = new Date(creationEntry.date);
          if (creationDate >= startOfCurrentWeek && creationDate <= endOfCurrentWeek) {
            return total + calculateScoreForLead(lead, configs);
          }
        }
        return total;
      }, 0);

      // Calculate current month points (leads created this month)
      const currentMonthPoints = userLeads.reduce((total, lead) => {
        const creationEntry = lead.history?.find(entry => entry.type === 'created');
        if (creationEntry && creationEntry.date) {
          const creationDate = new Date(creationEntry.date);
          if (creationDate >= startOfCurrentMonth && creationDate <= endOfCurrentMonth) {
            return total + calculateScoreForLead(lead, configs);
          }
        }
        return total;
      }, 0);

      // Calculate historical highs by scanning all periods
      const { weeklyHigh, monthlyHigh } = calculateHistoricalHighs(userLeads, configs);

      const userScoreRef = doc(db, "userScores", user.id);
      batch.set(userScoreRef, {
        userId: user.id,
        userName: user.name || user.email || "Unbekannter Benutzer",
        totalPoints: totalPoints,
        allTimeHigh: totalPoints,
        weeklyHigh: weeklyHigh,
        monthlyHigh: monthlyHigh,
        currentWeekPoints: currentWeekPoints,
        currentMonthPoints: currentMonthPoints,
        lastUpdated: now,
        lastWeekReset: startOfCurrentWeek,
        lastMonthReset: startOfCurrentMonth,
      }, { merge: true });

      logger.log(`DEV: User ${user.id} - Total: ${totalPoints}, Week: ${currentWeekPoints}, Month: ${currentMonthPoints}, WeeklyHigh: ${weeklyHigh}, MonthlyHigh: ${monthlyHigh}, Leads: ${userLeads.length}`);
    }

    await batch.commit();
    logger.log("DEV: Successfully recalculated all user scores");
  } catch (error) {
    logger.error("DEV: Error recalculating scores:", error);
    throw error;
  }
}

/**
 * DEV ONLY: Reset all high scores to current values
 * This function should only be used in development/testing to fix corrupted high score data
 */
export async function devResetHighScores(): Promise<void> {
  try {
    logger.log("DEV: Starting high score reset");
    const scoresRef = collection(db, "userScores");
    const scoresSnapshot = await getDocs(scoresRef);

    const batch = writeBatch(db);
    const now = new Date();

    scoresSnapshot.forEach(doc => {
      const data = doc.data() as UserScore;
      const userScoreRef = doc.ref;

      // Reset high scores to 0 since existing values are corrupted by old assignedTo logic (Altkontakte)
      // The new period-based system will build correct high scores going forward
      batch.update(userScoreRef, {
        allTimeHigh: 0,
        weeklyHigh: 0,
        monthlyHigh: 0,
        currentWeekPoints: 0,
        currentMonthPoints: 0,
        totalPoints: 0,
        lastUpdated: now
      });
    });

    await batch.commit();
    logger.log("DEV: Successfully reset high scores for all users");
  } catch (error) {
    logger.error("DEV: Error resetting high scores:", error);
    throw error;
  }
}

/**
 * DEV ONLY: Manually adjusts the week for all users
 * This function should only be used in development/testing
 * @param weeksToAdd Number of weeks to add (positive) or subtract (negative)
 */
export async function devAdjustWeek(weeksToAdd: number): Promise<void> {
  try {
    logger.log(`DEV: Starting manual week adjustment by ${weeksToAdd} weeks`);
    const scoresRef = collection(db, "userScores");
    const scoresSnapshot = await getDocs(scoresRef);

    const batch = writeBatch(db);
    const now = new Date();
    const adjustedDate = addWeeks(now, weeksToAdd);
    const startOfAdjustedWeek = startOfDay(subDays(adjustedDate, adjustedDate.getDay()));
    const endOfAdjustedWeek = addDays(startOfAdjustedWeek, 6);
    const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfAdjustedMonth = new Date(adjustedDate.getFullYear(), adjustedDate.getMonth(), 1);
    const endOfAdjustedMonth = new Date(adjustedDate.getFullYear(), adjustedDate.getMonth() + 1, 0);

    // If we're moving to a future date, reset all points
    const isFutureDate = adjustedDate > now;

    scoresSnapshot.forEach(doc => {
      const data = doc.data() as UserScore;
      const userScoreRef = doc.ref;

      const updates: Partial<UserScore> = {
        lastWeekReset: startOfAdjustedWeek,
        lastUpdated: now,
      };

      if (isFutureDate) {
        // Reset all points for future dates
        updates.currentWeekPoints = 0;
        updates.currentMonthPoints = 0;
        updates.lastMonthReset = startOfAdjustedMonth;
        logger.log(`Future date detected, resetting all points for user ${data.userId}`);
      } else {
        // For past dates, only update the reset dates
        // The actual points will be recalculated based on leads in that period
        updates.lastMonthReset = startOfAdjustedMonth;
      }

      batch.update(userScoreRef, updates);
      logger.log(`Updated scores for user ${data.userId}:`, updates);
    });

    await batch.commit();
    logger.log(`DEV: Successfully adjusted week by ${weeksToAdd} weeks for all users`);
  } catch (error) {
    logger.error("DEV: Error adjusting week:", error);
    throw error;
  }
}

/**
 * Determines the correct score action ID based on invited user position
 * @param position The position of the invited user
 * @returns The score action ID to use
 */
function getInvitationScoreActionId(position: string): string {
  switch (position) {
    case 'praktikant':
    case 'dual-student':
      return 'invitation_praktikant_activated';
    case 'berater':
    case 'trainee':
    case 'beraterassistent':
      return 'invitation_berater_activated';
    default:
      return 'invitation_praktikant_activated'; // Default fallback
  }
}

/**
 * Awards points for a successful invitation activation using position-specific score action
 * @param inviterUserId The user who made the invitation
 * @param pointsAmount The amount of points to award (250 for Praktikant, 1000 for Berater/Trainee)
 * @param invitedUserPosition The position of the invited user
 */
export async function awardInvitationPoints(
  inviterUserId: string,
  pointsAmount: number,
  invitedUserPosition: string,
  tenantId: string = "legacy"
): Promise<boolean> {
  try {
    logger.log(`Awarding ${pointsAmount} invitation points to user ${inviterUserId} for inviting ${invitedUserPosition}`);

    // Get or create user score document
    const userScoreRef = doc(db, "userScores", inviterUserId);
    const userScoreSnap = await getDoc(userScoreRef);

    const now = new Date();
    const startOfCurrentWeek = startOfDay(subDays(now, now.getDay()));
    const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    if (userScoreSnap.exists()) {
      const existingScore = userScoreSnap.data() as UserScore;

      // Update existing score
      await updateDoc(userScoreRef, {
        currentWeekPoints: (existingScore.currentWeekPoints || 0) + pointsAmount,
        currentMonthPoints: (existingScore.currentMonthPoints || 0) + pointsAmount,
        totalPoints: (existingScore.totalPoints || 0) + pointsAmount,
        allTimeHigh: Math.max(existingScore.allTimeHigh || 0, (existingScore.totalPoints || 0) + pointsAmount),
        weeklyHigh: Math.max(existingScore.weeklyHigh || 0, (existingScore.currentWeekPoints || 0) + pointsAmount),
        monthlyHigh: Math.max(existingScore.monthlyHigh || 0, (existingScore.currentMonthPoints || 0) + pointsAmount),
        lastUpdated: now,
        tenantId: tenantId
      });
    } else {
      // Create new score document
      const newUserScore: UserScore = {
        userId: inviterUserId,
        userName: "Benutzer", // Will be updated by next score calculation
        totalPoints: pointsAmount,
        allTimeHigh: pointsAmount,
        weeklyHigh: pointsAmount,
        monthlyHigh: pointsAmount,
        currentWeekPoints: pointsAmount,
        currentMonthPoints: pointsAmount,
        lastUpdated: now,
        lastWeekReset: startOfCurrentWeek,
        lastMonthReset: startOfCurrentMonth,
        tenantId: tenantId
      };

      await setDoc(userScoreRef, newUserScore);
    }

    logger.log(`Successfully awarded ${pointsAmount} invitation points to user ${inviterUserId}`);
    return true;
  } catch (error) {
    logger.error("Error awarding invitation points:", error);
    return false;
  }
}

/**
 * DEV ONLY: Removes deprecated score configurations from Firestore
 * This function should be called once to clean up old configurations
 */
export async function removeDeprecatedScoreConfigs(): Promise<void> {
  try {
    logger.log("DEV: Starting removal of deprecated score configurations");

    // List of deprecated configuration IDs to remove
    const deprecatedIds = ['user_invitation_activated'];

    const batch = writeBatch(db);

    for (const deprecatedId of deprecatedIds) {
      const docRef = doc(db, SCORE_CONFIG_COLLECTION, deprecatedId);
      batch.delete(docRef);
      logger.log(`DEV: Marked for deletion: ${deprecatedId}`);
    }

    await batch.commit();
    logger.log("DEV: Successfully removed deprecated score configurations");
  } catch (error) {
    logger.error("DEV: Error removing deprecated score configurations:", error);
    throw error;
  }
}

// --- Future functions for score calculation will go here --- //
// e.g., calculateUserScores(userId, leads)
// e.g., awardPointsForAction(userId, actionType, lead?) 
